import { API_ENDPOINT } from "@/constants/appConstants";

import { EducationDataType } from "@/components/profile/educationCard/types";
import {
  BasicDetailsPrefillType,
  EducationPrefillType,
  EmploymentPrefillType,
  JobTitlePrefillType,
  ProfileDataType,
  SkillsPrefillType,
} from "@/components/registration/edit-profile/types";

export const getEducationPrefillData = (educations: any) => {
  let educationPrefillData: EducationDataType = {
    id: educations?.id ?? "",
    educationType: {
      id: educations?.educationType?.id ?? "",
      text: educations?.educationType?.text ?? "",
    },
    highestQualification: {
      id: educations?.highestQualification?.id ?? "",
      text: educations?.highestQualification?.text ?? "",
    },
    gradingSystem: educations?.gradingSystem ?? "",
    marks: educations?.marks ?? 0,
    yearOfPassing: educations?.yearOfPassing ?? 0,
  };

  if (educations?.board) {
    educationPrefillData["board"] = {
      id: educations?.board?.id ?? "",
      text: educations?.board?.text ?? "",
    };
  }

  if (educations?.medium) {
    educationPrefillData["medium"] = {
      id: educations?.medium?.id ?? "",
      text: educations?.medium?.text ?? "",
    };
  }

  if (educations?.college) {
    educationPrefillData["college"] = {
      id: educations?.college?.id ?? "",
      text: educations?.college?.text ?? "",
    };
  }

  if (educations?.specialization) {
    educationPrefillData["specialization"] = {
      id: educations?.specialization?.id ?? "",
      text: educations?.specialization?.text ?? "",
    };
  }

  return { educationPrefillData };
};

export const getBasicDetailsPrefillData = async (
  personalDetails: any,
  hostname: string,
) => {
  let masterData: any = null;
  let isSameCountry = false;
  try {
    const masterDataRequest = await fetch(
      `${API_ENDPOINT(hostname).middleware.masterDataMiddleware}?appName=srp-ui&type=location&limit=10&name=${personalDetails?.additionalPersonalDetail?.currentLocation?.text}`,
    );

    masterData = await masterDataRequest.json();
  } catch (error) {
    console.error("Error fetching master data", error);
  }

  if (masterData) {
    const masterDataList = masterData?.data;
    if (masterDataList && masterDataList.length > 0) {
      // find exact match of current location with master data
      const masterDataItem = masterDataList.find((item: any) => {
        return (
          item?.name ===
          personalDetails?.additionalPersonalDetail?.currentLocation?.text
        );
      });

      if (
        masterDataItem &&
        masterDataItem?.country?.uuid ===
          personalDetails?.nationality?.extraInfo?.countryUuId
      ) {
        // SAME LOCATION
        isSameCountry = true;
      }
    }
  }

  const basicDetailsPrefill: BasicDetailsPrefillType = {
    fullName: personalDetails?.fullName ?? "",
    experienceLevel: personalDetails?.experienceLevel ?? "",
    additionalPersonalDetail: {
      experience: {
        years:
          personalDetails?.additionalPersonalDetail?.experience?.years ?? 0,
        months:
          personalDetails?.additionalPersonalDetail?.experience?.months ?? 0,
      },
      currentLocation: {
        id:
          personalDetails?.additionalPersonalDetail?.currentLocation?.id ?? "",
        text:
          personalDetails?.additionalPersonalDetail?.currentLocation?.text ??
          "",
      },
    },
  };

  if (personalDetails?.additionalPersonalDetail?.gender) {
    basicDetailsPrefill.additionalPersonalDetail["gender"] = {
      text: personalDetails?.additional,
    };
  }

  if (!isSameCountry) {
    // Add nationality and workVisaType only if current location is different
    basicDetailsPrefill.additionalPersonalDetail["nationality"] = {
      id: personalDetails?.nationality?.id ?? "",
      text: personalDetails?.nationality?.text ?? "",
    };

    //! TEST THIS
    if (personalDetails?.additionalPersonalDetail?.workVisaType) {
      basicDetailsPrefill.additionalPersonalDetail["workVisaType"] = {
        id: personalDetails?.additionalPersonalDetail?.workVisaType?.id ?? "",
        text:
          personalDetails?.additionalPersonalDetail?.workVisaType?.text ?? "",
      };
    }
  }

  return { basicDetailsPrefill };
};

export const getWorkExperiencePrefillData = (employments: any) => {
  if (!employments || employments.length === 0) return null;

  //* Discussed with Arpit, 0th index is the current work experience
  let currentWorkExperience = employments[0];

  const employmentPrefill: EmploymentPrefillType = {
    id: currentWorkExperience?.id ?? "",
    designation: {
      id: currentWorkExperience?.designation?.id ?? "",
      text: currentWorkExperience?.designation?.text ?? "",
    },
    company: {
      id: currentWorkExperience?.company?.id ?? "",
      text: currentWorkExperience?.company?.text ?? "",
    },
    startDate: currentWorkExperience?.startDate ?? "",
    salary: {
      currency: currentWorkExperience?.salary?.currency ?? "",
      absoluteValue: currentWorkExperience?.salary?.absoluteValue ?? "",
      salaryMode: {
        id: currentWorkExperience?.salary?.salaryMode?.id ?? "",
        text: currentWorkExperience?.salary?.salaryMode?.text ?? "",
      },
      confidential: currentWorkExperience?.salary?.confidential ?? "",
    },
  };

  if (currentWorkExperience?.endDate) {
    employmentPrefill["endDate"] = currentWorkExperience?.endDate;
  }

  if (currentWorkExperience?.noticePeriod) {
    employmentPrefill["noticePeriod"] = {
      days: currentWorkExperience?.noticePeriod?.days ?? "",
      serving: currentWorkExperience?.noticePeriod?.serving ?? "",
      lastWorkingDay: currentWorkExperience?.noticePeriod?.lastWorkingDay ?? "",
    };
  }

  if (currentWorkExperience?.currentWorkExperience?.offeredSalary) {
    employmentPrefill["offeredSalary"] = {
      currency:
        currentWorkExperience?.currentWorkExperience?.offeredSalary?.currency ??
        "",
      absoluteValue:
        currentWorkExperience?.currentWorkExperience?.offeredSalary
          ?.absoluteValue ?? "",
      salaryMode: {
        id:
          currentWorkExperience?.currentWorkExperience?.offeredSalary
            ?.salaryMode?.id ?? "",
        text:
          currentWorkExperience?.currentWorkExperience?.offeredSalary
            ?.salaryMode?.text ?? "",
      },
      confidential:
        currentWorkExperience?.currentWorkExperience?.offeredSalary
          ?.confidential ?? "",
    };
  }

  return { employmentPrefill };
};

export const getHighestEducationPrefillData = (educations: any) => {
  if (!educations || educations.length === 0) return null;

  let highestEducation = educations[0];
  let highestYearOfPassing = educations[0]?.yearOfPassing ?? 0;
  for (let i = 0; i < educations.length; i++) {
    if (highestYearOfPassing < educations[i].yearOfPassing) {
      highestYearOfPassing = educations[i].yearOfPassing;
      highestEducation = educations[i];
    }
  }

  let educationPrefillData: EducationPrefillType = {
    id: highestEducation?.id ?? "",
    educationType: {
      id: highestEducation?.educationType?.id ?? "",
      text: highestEducation?.educationType?.text ?? "",
    },
    highestQualification: {
      id: highestEducation?.highestQualification?.id ?? "",
      text: highestEducation?.highestQualification?.text ?? "",
    },
    gradingSystem: highestEducation?.gradingSystem ?? "",
    marks: highestEducation?.marks ?? 0,
    yearOfPassing: highestEducation?.yearOfPassing ?? 0,
  };

  if (highestEducation?.board) {
    educationPrefillData["board"] = {
      id: highestEducation?.board?.id ?? "",
      text: highestEducation?.board?.text ?? "",
    };
  }

  if (highestEducation?.medium) {
    educationPrefillData["medium"] = {
      id: highestEducation?.medium?.id ?? "",
      text: highestEducation?.medium?.text ?? "",
    };
  }

  if (highestEducation?.college) {
    educationPrefillData["college"] = {
      id: highestEducation?.college?.id ?? "",
      text: highestEducation?.college?.text ?? "",
    };
  }

  if (highestEducation?.specialization) {
    educationPrefillData["specialization"] = {
      id: highestEducation?.specialization?.id ?? "",
      text: highestEducation?.specialization?.text ?? "",
    };
  }

  return { educationPrefillData };
};

export const getSkillsPrefillData = (itSkills: any, skills: any) => {
  let skillsPrefill: SkillsPrefillType = {
    skills: [],
  };

  if (itSkills && Array.isArray(itSkills) && itSkills.length > 0) {
    skillsPrefill["skills"] = itSkills.map((skill: any) => ({
      id: skill?.skill?.id,
      text: skill?.skill?.text,
      dbId: skill?.id,
    }));
  }

  if (skills && Array.isArray(skills) && skills.length > 0) {
    const skillsData = skills.map((skill: any) => ({
      id: skill?.skill?.id,
      text: skill?.skill?.text,
      dbId: skill?.id,
    }));

    skillsPrefill["skills"] =
      skillsPrefill["skills"].length > 0
        ? [...skillsPrefill["skills"], ...skillsData]
        : skillsData;
  }

  return { skillsPrefill };
};

export const getPreferedJobTitlePrefillData = (jobPreference: any) => {
  if (!jobPreference) return null;

  let preferedJobTitlePrefillData: JobTitlePrefillType = {
    roles: [],
  };

  if (jobPreference?.roles && jobPreference?.roles.length > 0) {
    preferedJobTitlePrefillData = {
      roles: jobPreference?.roles.map((role: any) => ({
        id: role?.id,
        text: role?.text,
      })),
    };
  }

  return { preferedJobTitlePrefillData };
};

export const getProfilePrefillData = async (
  profileAPIData: any,
  hostname: string,
) => {
  const yearExperience =
    profileAPIData?.personalDetailSection?.personalDetails
      ?.additionalPersonalDetail?.experience?.years ?? 0;
  const monthExperience =
    profileAPIData?.personalDetailSection?.personalDetails
      ?.additionalPersonalDetail?.experience?.months ?? 0;

  const basicDetailsPrefillData = await getBasicDetailsPrefillData(
    (profileAPIData as any)?.personalDetailSection?.personalDetails,
    hostname,
  );

  const workExperiencePrefillData = getWorkExperiencePrefillData(
    (profileAPIData as any)?.employmentDetailSection?.employments,
  );

  const highestEducationPrefillData = getHighestEducationPrefillData(
    (profileAPIData as any)?.educationDetailSection?.educations,
  );

  const skillsPrefillData = getSkillsPrefillData(
    (profileAPIData as any)?.itSkillSection?.itSkills,
    (profileAPIData as any)?.skillSection?.skills,
  );

  const jobPreferencePrefillData = getPreferedJobTitlePrefillData(
    (profileAPIData as any)?.jobPreferenceSection?.jobPreference,
  );

  const profilePrefillData: ProfileDataType = {
    basicDetails: {
      fullName: (profileAPIData as any)?.personalDetailSection?.personalDetails
        ?.fullName,
      currentLocation: (profileAPIData as any)?.personalDetailSection
        ?.personalDetails?.additionalPersonalDetail?.currentLocation?.text,
      totalExperience: `${yearExperience} yrs, ${monthExperience} months`,
      experienceLevel: (profileAPIData as any)?.personalDetailSection
        ?.personalDetails?.experienceLevel,
    },
    latestWorkExp: {
      company:
        workExperiencePrefillData?.employmentPrefill?.company?.text ?? "",
      designation:
        workExperiencePrefillData?.employmentPrefill?.designation?.text ?? "",
      startDate: workExperiencePrefillData?.employmentPrefill?.startDate ?? "",
    },
    keySkills: skillsPrefillData?.skillsPrefill?.skills ?? [],
    highestEducation: {
      university: highestEducationPrefillData?.educationPrefillData?.college
        ?.text
        ? highestEducationPrefillData?.educationPrefillData?.college?.text
        : highestEducationPrefillData?.educationPrefillData?.board?.text
          ? highestEducationPrefillData?.educationPrefillData?.board?.text
          : "",
      degree:
        highestEducationPrefillData?.educationPrefillData?.highestQualification
          ?.text ?? "",
    },
  };

  if (workExperiencePrefillData?.employmentPrefill?.endDate) {
    profilePrefillData.latestWorkExp["endDate"] =
      workExperiencePrefillData?.employmentPrefill?.endDate;
  }

  if (highestEducationPrefillData?.educationPrefillData?.yearOfPassing) {
    profilePrefillData.highestEducation["yearOfPassing"] =
      highestEducationPrefillData?.educationPrefillData?.yearOfPassing;
  }

  if (
    jobPreferencePrefillData?.preferedJobTitlePrefillData?.roles &&
    jobPreferencePrefillData?.preferedJobTitlePrefillData?.roles.length > 0
  ) {
    profilePrefillData.preferredJobTitle =
      jobPreferencePrefillData?.preferedJobTitlePrefillData?.roles;
  }

  return {
    profilePrefillData,
    basicDetailsPrefillData,
    workExperiencePrefillData,
    highestEducationPrefillData,
    skillsPrefillData,
    jobPreferencePrefillData,
  };
};
